// ExplainTree — renders MySQL/MariaDB `EXPLAIN FORMAT=JSON` output as a
// collapsible cost/rows tree, with a Raw-JSON toggle and a per-node cost
// heatmap so the hotspot in the plan jumps out without reading every row.
//
// Design: rather than modelling MySQL's plan grammar (which differs across
// 5.6 / 8.0 / MariaDB), it walks the JSON generically and surfaces the fields
// that answer "why is this slow?" — table, access type, key, estimated rows,
// and cost — as a one-line summary on each node. This stays correct across
// vendors/versions; the Raw view is always available as the ground truth.
//
// Cost heatmap: each node's read_cost (falling back to prefix_cost / query_cost)
// is computed as a fraction of the largest node-cost seen in the tree, then
// painted as a background tint from cool to warm. The single hottest node also
// gets a 🔥 marker so a fast scroll can spot it. Nodes without a cost number
// (MariaDB versions, some subquery shapes) stay neutral — we don't fake heat.
//
// Plan+Actual overlay: when an `analyzeJson` prop is supplied alongside `json`,
// the renderer indexes the ANALYZE actual_rows per (table_name, access_type),
// then renders extra "actual: N" badges on each matching node. Where the
// estimate is >2× off, the badge tints warning-coloured — that's the
// misestimate the user wants to see.
//
// Props:
//   json         string  — required, the FORMAT=JSON output of a plain EXPLAIN
//   analyzeJson  string  — optional, the FORMAT=JSON output of EXPLAIN ANALYZE
//                          (or MariaDB's ANALYZE FORMAT=JSON, which carries
//                          the r_rows / r_total_time_ms enrichment)
//   analyzeText  string  — optional fallback, the TREE-format text from
//                          EXPLAIN ANALYZE on MySQL 8.0.18+ (pre-8.0.32).
//                          We don't merge from it — just surface it below the
//                          tree so the actual numbers aren't lost.
import { createSignal, createMemo, For, Show } from 'solid-js';
import { toast } from '../lib/toast.js';
import { copyText } from '../lib/export.js';

const isObj = (v) => v && typeof v === 'object';
const num = (v) => (typeof v === 'number' ? v.toLocaleString() : String(v));

// Numeric extractor — MySQL serialises cost as quoted strings ("10.5"), MariaDB
// as bare numbers (10.5). Accept both; treat anything non-finite as missing.
const toNum = (v) => {
  if (v == null) return null;
  const n = typeof v === 'number' ? v : parseFloat(v);
  return Number.isFinite(n) ? n : null;
};

// Best-available "this node's cost" — prefers a per-step read_cost, falls back
// to prefix_cost (running total to this point), then query_cost (root only).
function nodeCost(d) {
  if (!isObj(d) || Array.isArray(d)) return null;
  const ci = isObj(d.cost_info) ? d.cost_info : null;
  return toNum(ci?.read_cost)
      ?? toNum(ci?.prefix_cost)
      ?? toNum(ci?.query_cost)
      ?? toNum(d.query_cost);
}

// Walk the whole plan once to gather (a) the largest single-node cost — used as
// the heatmap denominator — and (b) the node with that max cost (for the 🔥
// marker). Pre-computed and threaded down via props so each node doesn't have
// to re-derive it. Returns {maxCost, hottestNode} where hottestNode is a
// reference into the parsed JSON.
function scanPlan(root) {
  let maxCost = 0;
  let hottest = null;
  const visit = (d) => {
    if (Array.isArray(d)) { for (const v of d) visit(v); return; }
    if (!isObj(d)) return;
    const c = nodeCost(d);
    if (c != null && c > maxCost) { maxCost = c; hottest = d; }
    for (const v of Object.values(d)) if (isObj(v)) visit(v);
  };
  visit(root);
  return { maxCost, hottest };
}

// Map a 0..1 share to a CSS background tint. Cold (low share) is a soft
// blue-grey, hot (high share) is a saturated red. We use color-mix so theme
// variables propagate (dark + light themes both look right). Below the
// floor we return '' — leaves the node un-tinted, no faux heat.
function heatBg(share) {
  if (share == null || share < 0.04) return ''; // below the floor — no tint
  const pct = Math.min(100, Math.round(share * 100));
  // Three tiers chosen to keep the tree readable: cool until ~25%, warm at
  // ~50%, hot above ~75%. Linear gradient would blend smoothly but the
  // discrete steps make the hottest cell unmistakable.
  if (share < 0.25) return `color-mix(in srgb, var(--accent) ${Math.round(8 + pct/2)}%, transparent)`;
  if (share < 0.5)  return `color-mix(in srgb, var(--warn,   #f59e0b) ${Math.round(14 + pct/3)}%, transparent)`;
  return                  `color-mix(in srgb, var(--danger, #ef4444) ${Math.round(18 + pct/3)}%, transparent)`;
}

// Index the analyzeJson by a stable per-node key (table_name + access_type) so
// we can join "estimated" and "actual" cheaply at render time. We accept three
// shapes for the actual-row field:
//   MySQL ANALYZE FORMAT=JSON      → "actual_rows"        (also "actual_loops",
//                                                          "actual_time_ms")
//   MariaDB ANALYZE FORMAT=JSON    → "r_rows"             (also "r_total_time_ms")
// Anything else → null (overlay tag won't render for that node).
function indexAnalyze(root) {
  if (!isObj(root)) return new Map();
  const out = new Map();
  const visit = (d) => {
    if (Array.isArray(d)) { for (const v of d) visit(v); return; }
    if (!isObj(d)) return;
    const tn = d.table_name;
    if (tn) {
      const k = tn + '|' + (d.access_type || '');
      const actualRows = toNum(d.actual_rows) ?? toNum(d.r_rows);
      const actualMs   = toNum(d.actual_time_ms) ?? toNum(d.r_total_time_ms);
      const actualLoops = toNum(d.actual_loops) ?? toNum(d.r_loops);
      if (actualRows != null || actualMs != null || actualLoops != null) {
        out.set(k, { actualRows, actualMs, actualLoops });
      }
    }
    for (const v of Object.values(d)) if (isObj(v)) visit(v);
  };
  visit(root);
  return out;
}

// Pull the matching actual measurements for a node — returns null if the node
// isn't a table step, or if the analyze index has no entry. estimated-vs-actual
// delta is computed by the caller for the badge colour.
function actualFor(d, analyzeIndex) {
  if (!analyzeIndex || !isObj(d) || !d.table_name) return null;
  return analyzeIndex.get(d.table_name + '|' + (d.access_type || '')) || null;
}

// Scalar keys worth a highlight class in the per-node detail list.
const HILITE = {
  access_type: 'acc', key: 'key', possible_keys: 'dim', used_key_parts: 'dim',
  rows_examined_per_scan: 'rows', rows_produced_per_join: 'rows', rows: 'rows',
  filtered: 'flt', attached_condition: 'dim',
  using_filesort: 'warn', using_temporary_table: 'warn',
  query_cost: 'cost', read_cost: 'cost', eval_cost: 'cost', prefix_cost: 'cost',
};

// A short badge summary of the salient metrics on one object node. When
// `actual` (from indexAnalyze) is supplied, also emit "actual rows" and
// "actual time" badges, with a warning tint when estimate vs actual are off
// by more than ESTIMATE_DELTA_X — this is THE thing the user wants to see
// in a Plan+Actual view: where the planner guessed wrong.
const ESTIMATE_DELTA_X = 2; // 2× off is a meaningful misestimate
function summarize(data, actual) {
  if (!isObj(data) || Array.isArray(data)) return [];
  const s = [];
  if (data.access_type) s.push(['acc', data.access_type]);
  if (data.key) s.push(['key', `key: ${data.key}`]);
  const rows = data.rows_examined_per_scan ?? data.rows_produced_per_join ?? data.rows;
  if (rows != null) s.push(['rows', `rows≈${num(rows)}`]);
  const cost = (isObj(data.cost_info) && (data.cost_info.query_cost ?? data.cost_info.prefix_cost)) ?? data.query_cost;
  if (cost != null) s.push(['cost', `cost ${num(cost)}`]);
  if (data.using_filesort) s.push(['warn', 'filesort']);
  if (data.using_temporary_table) s.push(['warn', 'temporary']);
  // Actual-rows / actual-time badges from the ANALYZE join (Phase 2).
  if (actual) {
    if (actual.actualRows != null) {
      const est = toNum(rows);
      // delta = max(actual/est, est/actual). >2× either way is a misestimate.
      let cls = 'actual';
      if (est != null && est > 0 && actual.actualRows > 0) {
        const d = Math.max(actual.actualRows / est, est / actual.actualRows);
        if (d >= ESTIMATE_DELTA_X) cls = 'misest';
      }
      s.push([cls, `actual ${num(actual.actualRows)}`]);
    }
    if (actual.actualMs != null) s.push(['actual', `${num(actual.actualMs)}ms`]);
  }
  return s;
}

function PlanNode(props) {
  const depth = () => props.depth ?? 0;
  const [open, setOpen] = createSignal(depth() < 4); // top levels expanded
  const data = () => props.data;
  const isArr = () => Array.isArray(data());
  const obj = () => isObj(data()) && !isArr();
  const entries = () => (obj() ? Object.entries(data()) : []);
  const scalars = () => entries().filter(([k, v]) => !isObj(v) && k !== 'table_name');
  const children = () => (isArr()
    ? data().map((v, i) => [String(i), v])
    : entries().filter(([, v]) => isObj(v)));
  const hasChildren = () => children().length > 0;

  // Cost heatmap — share of this node's cost vs the tree-wide max. Skipped
  // for arrays (containers, not steps) and for nodes whose cost wasn't
  // reported (some MariaDB sub-queries, FT/index-only nodes). The hottest
  // node also gets a 🔥 marker so it stands out beyond the colour.
  const heat = createMemo(() => {
    if (isArr()) return { share: null, hottest: false };
    const c = nodeCost(data());
    if (c == null || !props.maxCost) return { share: null, hottest: false };
    return { share: c / props.maxCost, hottest: data() === props.hottestNode };
  });
  const headStyle = () => {
    const bg = heatBg(heat().share);
    return bg ? `background:${bg};` : '';
  };

  const title = () => {
    if (isArr()) return `${props.label} [${data().length}]`;
    if (obj() && data().table_name) return `📋 ${data().table_name}`;
    return props.label;
  };

  // The Plan+Actual overlay: look up actual measurements for this node from
  // the analyzeIndex (passed down via props). Null for non-table nodes or
  // when no analyze data is present.
  const actual = () => actualFor(data(), props.analyzeIndex);

  return (
    <div class={`explain-node ${depth() === 0 ? 'explain-node-root' : ''}`}>
      <div class="explain-node-head" style={headStyle()} onClick={() => hasChildren() && setOpen(!open())}>
        <Show when={hasChildren()} fallback={<span class="explain-node-bullet">•</span>}>
          <span class="explain-node-caret">{open() ? '▾' : '▸'}</span>
        </Show>
        <span class="explain-node-title">{title()}</span>
        <Show when={heat().hottest}>
          <span class="explain-hot-marker" title="Hottest node in this plan — highest read_cost">🔥</span>
        </Show>
        <For each={summarize(data(), actual())}>
          {([cls, text]) => <span class={`explain-badge explain-badge-${cls}`}>{text}</span>}
        </For>
      </div>
      <Show when={open()}>
        <div class="explain-node-body">
          <For each={scalars()}>
            {([k, v]) => (
              <div class={`explain-kv ${HILITE[k] ? 'explain-hl-' + HILITE[k] : ''}`}>
                <span class="explain-kv-key mono">{k}</span>
                <span class="explain-kv-val mono">{String(v)}</span>
              </div>
            )}
          </For>
          <For each={children()}>
            {([k, v]) => (
              <PlanNode data={v} label={k} depth={depth() + 1}
                        maxCost={props.maxCost} hottestNode={props.hottestNode}
                        analyzeIndex={props.analyzeIndex} />
            )}
          </For>
        </div>
      </Show>
    </div>
  );
}

export default function ExplainTree(props) {
  const [raw, setRaw] = createSignal(false);
  const parsed = createMemo(() => {
    try { return { ok: true, value: JSON.parse(props.json) }; }
    catch (e) { return { ok: false, error: e.message }; }
  });
  // ANALYZE JSON is optional — when present, indexAnalyze gives us a
  // (table_name + access_type) → {actualRows, actualMs, actualLoops} map that
  // PlanNode reads via actualFor to render the overlay badges.
  const analyzeParsed = createMemo(() => {
    if (!props.analyzeJson) return null;
    try { return JSON.parse(props.analyzeJson); }
    catch (_) { return null; }
  });
  const analyzeIndex = createMemo(() => indexAnalyze(analyzeParsed()));
  // Cost-scan pass: max single-node cost (denominator for heat shares) + a
  // reference to the hottest node (for the 🔥 marker).
  const planScan = createMemo(() => (parsed().ok ? scanPlan(parsed().value) : { maxCost: 0, hottest: null }));
  const pretty = () => (parsed().ok ? JSON.stringify(parsed().value, null, 2) : (props.json || ''));
  const copy = async () => {
    try { await copyText(pretty()); toast.success('Plan JSON copied'); }
    catch (e) { toast.error('Copy failed: ' + e.message); }
  };

  return (
    <div class="explain-tree">
      <div class="explain-tree-toolbar">
        <button class={`btn btn-sm ${raw() ? 'btn-ghost' : ''}`} onClick={() => setRaw(false)}>Tree</button>
        <button class={`btn btn-sm ${raw() ? '' : 'btn-ghost'}`} onClick={() => setRaw(true)}>Raw JSON</button>
        <Show when={props.analyzeJson || props.analyzeText}>
          <span class="explain-mode-pill" title="Plan + Actual mode — actual_rows / actual_time merged from EXPLAIN ANALYZE">
            ⚡ with actuals
          </span>
        </Show>
        <div class="spacer" />
        <Show when={planScan().maxCost > 0}>
          <span class="muted mono" style="font-size:11px;" title="Largest single-node read_cost. The heatmap colours scale from this maximum.">
            hotspot {num(Math.round(planScan().maxCost))}
          </span>
        </Show>
        <button class="btn btn-sm btn-ghost" onClick={copy}>⧉ Copy JSON</button>
      </div>
      <div class="explain-tree-body">
        <Show
          when={parsed().ok}
          fallback={
            <div class="muted" style="padding:12px;">
              Couldn’t parse plan JSON: {parsed().error}
              <pre class="explain-raw mono">{props.json}</pre>
            </div>
          }
        >
          <Show when={raw()} fallback={
            <PlanNode data={parsed().value} label="query plan" depth={0}
                      maxCost={planScan().maxCost} hottestNode={planScan().hottest}
                      analyzeIndex={analyzeIndex()} />
          }>
            <pre class="explain-raw mono">{pretty()}</pre>
          </Show>
        </Show>
        {/* Fallback panel: when the server gave us EXPLAIN ANALYZE in TREE text
            (MySQL 8.0.18+ pre-8.0.32), we can't merge it into the tree by
            field, but we can surface it below so the actuals aren't lost. */}
        <Show when={props.analyzeText && !props.analyzeJson}>
          <div class="explain-analyze-aside">
            <div class="explain-analyze-aside-head muted">
              <span>📊 EXPLAIN ANALYZE (TREE format — couldn't merge into plan tree)</span>
            </div>
            <pre class="explain-raw mono">{props.analyzeText}</pre>
          </div>
        </Show>
      </div>
    </div>
  );
}
